// Unified global search shared by the topbar preview dropdown and the /search
// results page, so both surfaces render the exact same sections in the same
// order from a single source of truth.
//
// Sections (priority order): Leads, Markets, Pages.
// Leads are matched against the encrypted IndexedDB mirror (the full local
// leads database), loaded lazily and memoized for the session.
import { useEffect, useState } from 'react'
import { leadHaystack, leadMatches } from './leadSearch.js'
import { getCachedCompanies, getMirrorGeneration } from './leadsCache.js'
import { keyOf } from './companyMeta.js'
import {
  CATEGORIES as CATEGORIES_CONFIG,
  CATEGORY_KEYS,
  CATEGORY_LABEL,
  categoryPath,
} from './categories.js'

const CATEGORIES = CATEGORY_KEYS

export { CATEGORY_LABEL }

// Static index of app destinations (command-palette style). Keywords widen
// matches beyond the visible label (e.g. "team" → Collaboration).
// `perm` marks pages that only exist for roles holding that permission. The
// search surfaces filter on it, so a page a role cannot open never APPEARS in
// its palette either — a member's search must not advertise surfaces their app
// does not have. No perm = visible to everyone.
export const PAGES = [
  { to: '/dashboard', label: 'Dashboard', group: 'General', keywords: 'home overview kpis', perm: 'pages.workspace' },
  // One palette entry per vertical in lib/categories.js.
  ...CATEGORIES_CONFIG.map((c) => ({
    to: categoryPath(c.key),
    label: c.plural,
    group: 'Leads',
    keywords: c.keywords,
  })),
  { to: '/performance/activity', label: 'Activity', group: 'Performance', keywords: 'team log', perm: 'pages.workspace' },
  { to: '/performance/sales', label: 'Sales', group: 'Performance', keywords: 'revenue stripe mrr arr subscriptions payments churn invoices', perm: 'pages.workspace' },
  { to: '/performance/software', label: 'Software', group: 'Performance', keywords: 'health uptime status errors error log heartbeat latency production environment incidents', perm: 'pages.workspace' },
  { to: '/logs/events', label: 'Events', group: 'Performance', keywords: 'audit log history', perm: 'events.view' },
  { to: '/collaboration', label: 'Collaboration', group: 'Workspace', keywords: 'team members invite' },
  { to: '/settings', label: 'Settings', group: 'Workspace', keywords: 'account preferences profile' },
]

// ---------- Company index (lazy, memoized for the session) ----------

let companiesPromise = null
let companiesGeneration = -1

// Loads both categories from the encrypted local mirror once, tags each row
// with its category, and precomputes a lowercased search haystack so per-query
// scans are plain substring checks. The index is re-read after any sync
// (lib/leadsCache.js bumps the mirror generation), and an empty result — the
// top bar focused before the leads page has synced — is not memoised at all,
// so the Leads section shows up on the next search rather than after a reload.
export function loadSearchCompanies() {
  if (companiesPromise && companiesGeneration === getMirrorGeneration()) return companiesPromise
  companiesGeneration = getMirrorGeneration()
  const promise = Promise.all(
    CATEGORIES.map(async (category) => {
      try {
        const { rows, stale } = await getCachedCompanies(category)
        if (stale || !rows) return []
        for (const r of rows) {
          r._category = category
          // The whole record, not six columns — a contact buried in the packed
          // blob must find the company from the top bar too (lib/leadSearch.js).
          leadHaystack(r)
        }
        return rows
      } catch {
        return []
      }
    }),
  ).then((lists) => {
    const all = lists.flat()
    if (!all.length && companiesPromise === promise) companiesPromise = null
    return all
  })
  companiesPromise = promise
  return promise
}

// React hook: resolves the company index once `active` becomes true (i.e. the
// user engages the search). Returns null until it's ready.
export function useSearchCompanies(active) {
  const [companies, setCompanies] = useState(null)
  useEffect(() => {
    if (!active) return
    let cancelled = false
    loadSearchCompanies().then((rows) => {
      if (!cancelled) setCompanies(rows)
    })
    return () => {
      cancelled = true
    }
  }, [active])
  return companies
}

// ---------- Highlight ----------

// Split `text` into [{ text, hit }] segments around every case-insensitive
// occurrence of `query`, so the UI can bold the matched runs.
export function splitHighlight(text, query) {
  const src = String(text ?? '')
  const needle = String(query ?? '').trim().toLowerCase()
  if (!needle) return [{ text: src, hit: false }]
  const lower = src.toLowerCase()
  const out = []
  let i = 0
  let idx = lower.indexOf(needle)
  while (idx !== -1) {
    if (idx > i) out.push({ text: src.slice(i, idx), hit: false })
    out.push({ text: src.slice(idx, idx + needle.length), hit: true })
    i = idx + needle.length
    idx = lower.indexOf(needle, i)
  }
  if (i < src.length) out.push({ text: src.slice(i), hit: false })
  return out
}

// ---------- Search ----------

function leadItem(r) {
  const id = keyOf(r)
  const subtitle =
    [r.city_state, r.trade].filter(Boolean).join(' · ') || CATEGORY_LABEL[r._category] || ''
  return {
    id: `lead:${id}`,
    icon: 'lead',
    title: r.name || 'Unnamed company',
    subtitle,
    to: `/leads/company/${encodeURIComponent(id)}`,
    // LeadDetail reads the full row from router state (there's no per-company
    // API), so hand it over exactly like the leads table does.
    state: { company: r, category: r._category },
  }
}

// Returns { sections, total }. Each section is
// { key, label, icon, items, total } where `items` is capped at `perSection`
// and `total` is the full match count. Only non-empty sections are included, so
// the topbar preview and the /search page always agree on which sections show.
// `can` is the caller's role check (useRbac().can). Defaults to allowing
// everything so non-UI callers keep working, but BOTH search surfaces pass it:
// results must never advertise a page the role's app does not have.
export function searchAll(query, { companies, manifest, perSection = 5, can = () => true } = {}) {
  const needle = String(query || '').trim().toLowerCase()
  if (!needle) return { sections: [], total: 0 }

  const sections = []

  // Leads — scanned against the full local mirror.
  if (companies && companies.length) {
    const items = []
    let count = 0
    for (const r of companies) {
      if (!leadMatches(r, needle)) continue
      count += 1
      if (items.length < perSection) items.push(leadItem(r))
    }
    if (count) sections.push({ key: 'leads', label: 'Leads', icon: 'lead', items, total: count })
  }

  // Markets — regions from the manifest, biggest first.
  const markets = []
  for (const [category, regions] of Object.entries(manifest?.categories || {})) {
    for (const rg of regions) {
      if (String(rg.label).toLowerCase().includes(needle)) markets.push({ category, ...rg })
    }
  }
  markets.sort((a, b) => (b.count || 0) - (a.count || 0))
  if (markets.length) {
    sections.push({
      key: 'markets',
      label: 'Markets',
      icon: 'market',
      total: markets.length,
      items: markets.slice(0, perSection).map((m) => ({
        id: `market:${m.category}/${m.slug}`,
        icon: 'market',
        title: m.label,
        subtitle: `${CATEGORY_LABEL[m.category] || m.category}${
          typeof m.count === 'number' ? ` · ${m.count.toLocaleString()} companies` : ''
        }`,
        to: `/leads/${m.category}?region=${encodeURIComponent(m.slug)}`,
      })),
    })
  }

  // Pages — static nav index, filtered to the pages this role's app has.
  const pages = PAGES.filter(
    (p) =>
      (!p.perm || can(p.perm)) &&
      `${p.label} ${p.group} ${p.keywords}`.toLowerCase().includes(needle),
  )
  if (pages.length) {
    sections.push({
      key: 'pages',
      label: 'Pages',
      icon: 'page',
      total: pages.length,
      items: pages.slice(0, perSection).map((p) => ({
        id: `page:${p.to}`,
        icon: 'page',
        title: p.label,
        subtitle: p.group,
        to: p.to,
      })),
    })
  }

  const total = sections.reduce((n, s) => n + s.total, 0)
  return { sections, total }
}
